import React, {
  useState,
  useCallback,
  useEffect,
} from "react";

import {
  Box,
  Button,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  makeStyles,
  Backdrop,
  CircularProgress,
} from "@material-ui/core";

import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DesktopWindowsIcon from "@material-ui/icons/DesktopWindows";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import {
  bannerList,
} from "../../../adminModel/setting";

import UploadImage from "./UploadImage";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  tableContainer: {
    width: "100%",
  },

  table: {
    minWidth: 900,
  },

  tableHead: {
    backgroundColor: "#f8fafc",
  },

  headCell: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    paddingTop: 14,
    paddingBottom: 14,
    borderBottom: "1px solid #e5e7eb",
  },

  bodyCell: {
    paddingTop: 14,
    paddingBottom: 14,
    borderBottom: "1px solid #f1f5f9",
    color: "#374151",
    fontSize: 13,
  },

  bannerCell: {
    width: 240,
  },

  bannerPreview: {
    width: 210,
    height: 85,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    backgroundColor: "#fafafa",
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },

  linkText: {
    maxWidth: 260,
    wordBreak: "break-word",
    lineHeight: 1.5,
    color: "#374151",
  },

  emptyText: {
    color: "#9ca3af",
    fontSize: 12,
  },

  statusChip: {
    fontSize: 11,
    fontWeight: 600,
    height: 28,
    borderRadius: 7,
  },

  actionButton: {
    minWidth: 100,
    height: 36,
    borderRadius: 7,
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
  },

  pagination: {
    borderTop: "1px solid #e5e7eb",
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  emptyRow: {
    height: 180,
  },

  emptyState: {
    textAlign: "center",
    color: "#9ca3af",
  },

  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 4,
  },

  emptyDescription: {
    fontSize: 12,
    color: "#9ca3af",
  },
}));

export default function View() {
  const classes = useStyles();

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [getData, setGetdata] = useState([]);

  const [openModal, setOpenModal] = useState(false);

  const [Data, setData] = useState("");

  /* =====================================================
     PAGINATION
  ===================================================== */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  /* =====================================================
     FETCH BANNERS
  ===================================================== */

  const FetchBanner = useCallback(async (keyword = "") => {
    setLoading(true);

    try {
      const results = await bannerList(keyword);

      setGetdata(
        Array.isArray(results)
          ? results
          : []
      );

      console.log("banner results:", results);
    } catch (error) {
      console.error(
        "Banner list error:",
        error
      );

      setGetdata([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    FetchBanner(search);
  }, [FetchBanner]);

  /* =====================================================
     UPLOAD MODAL
  ===================================================== */

  const handleClickUpload = (data) => {
    setData(data);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    FetchBanner(search);
  };

  const Loading = (data) => {
    console.log("status", data);
  };

  /* =====================================================
     PAGINATED DATA
  ===================================================== */

  const paginatedData = getData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Card className={classes.root}>

      {/* =================================================
          UPLOAD MODAL
      ================================================= */}

      <UploadImage
        open={openModal}
        GetData={Data}
        onClose={handleClose}
        Loading={() => Loading()}
      />

      {/* =================================================
          TABLE
      ================================================= */}

      <TableContainer className={classes.tableContainer}>

        <Table
          stickyHeader
          className={classes.table}
          aria-label="banner table"
        >

          {/* =========================
              HEADER
          ========================= */}

          <TableHead>
            <TableRow className={classes.tableHead}>

              <TableCell
                className={`${classes.headCell} ${classes.bannerCell}`}
              >
                Banner
              </TableCell>

              <TableCell className={classes.headCell}>
                Banner Link
              </TableCell>

              <TableCell
                className={classes.headCell}
                align="center"
              >
                Status
              </TableCell>

              <TableCell
                className={classes.headCell}
                align="center"
              >
                Layout
              </TableCell>

              <TableCell
                className={classes.headCell}
                align="center"
              >
                Action
              </TableCell>

            </TableRow>
          </TableHead>

          {/* =========================
              BODY
          ========================= */}

          <TableBody>

            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => {

                const bannerUrl =
                  row.banner_url ||
                  "https://placehold.co/600x300?text=NO+IMAGE";

                const isVisible =
                  Number(row.isVisible) === 1;

                const isMobile =
                  Number(row.isMobile) === 1;

                return (
                  <TableRow
                    hover
                    key={
                      row.id ||
                      `${page}-${index}`
                    }
                  >

                    {/* =====================
                        BANNER
                    ===================== */}

                    <TableCell
                      className={`${classes.bodyCell} ${classes.bannerCell}`}
                    >
                      <Box
                        className={
                          classes.bannerPreview
                        }
                        style={{
                          backgroundImage:
                            `url("${bannerUrl}")`,
                        }}
                      />
                    </TableCell>

                    {/* =====================
                        LINK
                    ===================== */}

                    <TableCell
                      className={classes.bodyCell}
                    >
                      {row.banner_link ? (
                        <Typography
                          className={
                            classes.linkText
                          }
                        >
                          {row.banner_link}
                        </Typography>
                      ) : (
                        <Typography
                          className={
                            classes.emptyText
                          }
                        >
                          No banner link
                        </Typography>
                      )}
                    </TableCell>

                    {/* =====================
                        STATUS
                    ===================== */}

                    <TableCell
                      className={classes.bodyCell}
                      align="center"
                    >

                      <Chip
                        icon={
                          isVisible ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )
                        }
                        label={
                          isVisible
                            ? "Show"
                            : "Hide"
                        }
                        size="small"
                        className={
                          classes.statusChip
                        }
                        variant="outlined"
                        color={
                          isVisible
                            ? "primary"
                            : "default"
                        }
                      />

                    </TableCell>

                    {/* =====================
                        LAYOUT
                    ===================== */}

                    <TableCell
                      className={classes.bodyCell}
                      align="center"
                    >

                      <Chip
                        icon={
                          isMobile ? (
                            <PhoneIphoneIcon />
                          ) : (
                            <DesktopWindowsIcon />
                          )
                        }
                        label={
                          isMobile
                            ? "Mobile"
                            : "Desktop"
                        }
                        size="small"
                        className={
                          classes.statusChip
                        }
                        variant="outlined"
                      />

                    </TableCell>

                    {/* =====================
                        ACTION
                    ===================== */}

                    <TableCell
                      className={classes.bodyCell}
                      align="center"
                    >

                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={
                          classes.actionButton
                        }
                        startIcon={
                          <CloudUploadIcon />
                        }
                        onClick={() =>
                          handleClickUpload(row)
                        }
                      >
                        Upload
                      </Button>

                    </TableCell>

                  </TableRow>
                );
              })
            ) : (

              /* =========================
                 EMPTY STATE
              ========================= */

              <TableRow>

                <TableCell
                  colSpan={5}
                  className={classes.emptyRow}
                >

                  <Box
                    className={
                      classes.emptyState
                    }
                  >

                    <Typography
                      className={
                        classes.emptyTitle
                      }
                    >
                      No banners found
                    </Typography>

                    <Typography
                      className={
                        classes.emptyDescription
                      }
                    >
                      There are no banner records
                      available.
                    </Typography>

                  </Box>

                </TableCell>

              </TableRow>

            )}

          </TableBody>

        </Table>

      </TableContainer>

      {/* =================================================
          PAGINATION
      ================================================= */}

      <TablePagination
        className={classes.pagination}
        rowsPerPageOptions={[
          10,
          25,
          100,
        ]}
        component="div"
        count={getData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={
          handleChangePage
        }
        onRowsPerPageChange={
          handleChangeRowsPerPage
        }
      />

      {/* =================================================
          LOADING
      ================================================= */}

      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </Card>
  );
}